/**
 * Gift visuals contract (SPEC_V8 §5). The gifts feature (tray, store, WS
 * handling) depends only on this file and `./index`. Everything under fx/
 * — art, renderers, face tracking glue, Polaroid compositor — is owned by
 * the effects module and may change freely behind these types.
 */

export const GIFT_IDS = [
  'rose', 'kulhad_chai', 'boba', 'matcha', 'love_note', 'sunflowers',
  'sparkles', 'fairy_lights', 'sakura', 'song',
  'coquette_bow', 'heart_shades', 'gajra', 'butterflies', 'golden_hour',
  'moon_stars', 'polaroid', 'flower_crown', 'disco', 'bouquet',
] as const;
export type GiftId = (typeof GIFT_IDS)[number];

/** card = short animated reveal; scene = ambient over both tiles; ar = on the recipient's face; moment = interactive. */
export type FxKind = 'card' | 'scene' | 'ar' | 'moment';

export interface GiftVisual {
  kind: FxKind;
  /** Default effect length (the server's `effect.duration_ms` wins). */
  durationMs: number;
  /** Accent colour for chips, toasts and the tray tile. */
  accent: string;
  /** Short display name (the server catalog name wins when present). */
  name: string;
  emoji: string;
}

/** Normalised (0..1 of the video frame) face pose, already un-mirrored. */
export interface FacePose {
  x: number;
  y: number;
  w: number;
  h: number;
  /** BlazeFace order: right eye, left eye, nose tip, mouth, right ear, left ear. */
  keypoints: { x: number; y: number }[];
  /** Head roll in radians (eye line). */
  roll: number;
}

export interface GiftFxProps {
  gift: GiftId;
  /**
   * The <video> of the tile this overlay decorates (AR effects track the face
   * in it). Null when no video (camera off) — AR falls back to the card reveal.
   */
  video: HTMLVideoElement | null;
  /** True when the tile is shown mirrored (self-view). */
  mirrored: boolean;
  /** Server `effect.started_at` (ms epoch) — effects late-join correctly. */
  startedAt: number;
  durationMs: number;
  /** Preset line / vibe id for love_note and song. */
  variant?: string | null;
  /** Resolved label for the variant (shown on the card). */
  variantLabel?: string | null;
  /** Sender's first name, for card captions. */
  fromName?: string;
  /** Called once when the effect has finished (or was removed). */
  onDone?: () => void;
  /**
   * Additive (fx module): a fixed, already-known face pose (normalised to the
   * video frame, or to the overlay itself when `video` is null). AR effects
   * skip tracking and use it as-is — for previews, screenshots and tests.
   */
  fixedPose?: FacePose | null;
  /** Additive: force reduced motion on/off (defaults to `prefers-reduced-motion`). */
  reducedMotion?: boolean;
}

/**
 * Overlay that fills its positioned parent (the video tile). `card` and
 * `scene` gifts ignore `video`; `ar` gifts track the face in `video`.
 */
export type GiftFxComponent = (props: GiftFxProps) => JSX.Element | null;

/** Static (or gently looping) illustration for the tray, shelf, store and chat cards. */
export interface GiftArtProps {
  gift: GiftId;
  size?: number;
  /** Loop a subtle preview animation (tray hover/focus). */
  animate?: boolean;
  className?: string;
}

export interface PolaroidInput {
  /** Left photo = me, right = them. Null if a camera is off. */
  me: HTMLVideoElement | null;
  them: HTMLVideoElement | null;
  /** Mirror my own frame like the self-view. */
  mirrorMe: boolean;
  names: [string, string];
  caption: string;
  date: Date;
}

/** Compose the Polaroid on device. Never uploads anything. */
export type ComposePolaroid = (input: PolaroidInput) => Promise<Blob>;

/** Preset Polaroid captions (handwritten style). */
export type PolaroidCaptions = readonly string[];

/** Additive: synced 3-2-1 countdown with a soft flash (SPEC_V8 §4.5). */
export interface PolaroidCountdownProps {
  /** `gift.moment {phase:'countdown', at}` (ms epoch, server clock). The shutter fires at `at + leadMs`. */
  at: number;
  /** Countdown length; default 3000 (3-2-1). */
  leadMs?: number;
  /** Clock to sync against (e.g. `() => serverClock.now()`); default `Date.now`. */
  now?: () => number;
  /** Called once, at the shutter moment (`at + leadMs`) — capture the frames here. */
  onCapture?: () => void;
  /** Called once after the flash has faded. */
  onDone?: () => void;
  reducedMotion?: boolean;
}

/* ------------------------------------------------------------------ */
/* Additive: multi-shot photo booth (strips, synced shutter)           */
/* ------------------------------------------------------------------ */

/** A captured still. Canvases are captured synchronously at the shutter instant. */
export type BoothImage = ImageBitmap | HTMLCanvasElement;

/** One shot: both people at the same (server-synchronised) instant. Null = camera off. */
export interface BoothFrame {
  /** My frame, already mirrored like my self-view when `mirrorMe` was set. */
  me: BoothImage | null;
  them: BoothImage | null;
  /** Shot index in the session (0-based). */
  index?: number;
  /** Scheduled shutter time (server clock, ms epoch). */
  at?: number;
  /** The pose prompt shown for this shot. */
  prompt?: string;
}

export interface PhotoBoothSessionProps {
  /** Server time (ms epoch) the shot sequence starts. Shot i fires at `at + 3000 + i * gapMs` on every device. */
  at: number;
  /** Number of shots; default 4. */
  shots?: number;
  /** Time between shots; default 3500. */
  gapMs?: number;
  me: HTMLVideoElement | null;
  them: HTMLVideoElement | null;
  /** Bake the self-view mirror into my captured frames. */
  mirrorMe: boolean;
  /** Clock to sync against (e.g. `() => serverClock.now()`); default `Date.now`. */
  now?: () => number;
  reducedMotion?: boolean;
  /** Called at each shutter, right after capture. */
  onShot?: (index: number) => void;
  /** Called once after the last shot with the captured frames (shots already past at mount are skipped). */
  onDone: (frames: BoothFrame[]) => void;
  /** When given, a Cancel button is shown; it stops the session (no onDone). */
  onCancel?: () => void;
}

export type BoothLayoutId = 'strip' | 'grid' | 'duo_strip' | 'polaroid';
export type BoothFilterId = 'natural' | 'bw' | 'film' | 'dreamy' | 'y2k' | 'sepia';
export type BoothThemeId =
  | 'classic' | 'coquette' | 'y2k' | 'negative' | 'chai' | 'marquee' | 'checker' | 'peacock'
  // v9 venues (append-only; the server's booth_theme variants)
  | 'rooftop' | 'sunset' | 'bookstore' | 'starry' | 'candlelight';

export interface BoothLayoutInfo {
  id: BoothLayoutId;
  name: string;
  /** Output size at scale 1 (composeBooth defaults to scale 2). */
  w: number;
  h: number;
  /** Frames the layout shows. */
  slots: number;
}
export interface BoothFilterInfo {
  id: BoothFilterId;
  name: string;
}
export interface BoothThemeInfo {
  id: BoothThemeId;
  name: string;
  /** Two colours for a picker chip. */
  swatch: [string, string];
}

export interface ComposeBoothInput {
  frames: BoothFrame[];
  layout: BoothLayoutId;
  theme: BoothThemeId;
  filter: BoothFilterId;
  /** [me, them] first names. */
  names: [string, string];
  caption?: string;
  date: Date;
  /** Output scale (1 = base size, 2 = high-res, the default). */
  scale?: number;
  /** `polaroid` layout: which frame to use (default: the last captured). */
  pick?: number;
}
/** Compose a booth print on device and return a JPEG. Never uploads anything. */
export type ComposeBooth = (input: ComposeBoothInput) => Promise<Blob>;

export interface BoothPreviewProps extends Omit<ComposeBoothInput, 'scale'> {
  /** Called with the freshly composed JPEG after every (debounced) change — wire Save / Share to it. */
  onBlob?: (blob: Blob) => void;
  /** Debounce for re-composing on layout/theme/filter changes; default 120 ms. */
  debounceMs?: number;
  /** Output scale for the blob; default 2. */
  scale?: number;
  className?: string;
}
